import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude } from '../helpers/stringFormat';
import { formatString, getMagnitude } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { createSliderMarks, getSliderInitialValue } from './formFunctions';
import { toolTipUserIcon } from './iconFunctions';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const CostValueDriverOperatingForm = forwardRef(({ currency, outcome, outcomeList, saving, costStructure, editOperatingDriverId, editOperatingDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [selectedType, setSelectedType] = useState(costStructure?.multiple_type[0] || '')
    const [positiveDriver, setPositiveDriver] = useState(true);

    let variableName
    if (saving) {
        variableName = positiveDriver ? 'Savings' : 'Extra costs'
    } else {
        variableName = `Efficiency ${positiveDriver ? 'gain' : 'loss'}`
    }

    const tooltipText = {
        efficiency: `${variableName} relative to your competitive alternative (the one stated when you created your Product).`,
        shareOfCost: "Indicates the percentage of cost affected by the efficiency change (gain or loss). E.g., 100% means all of the cost are affected; 50% means half are.",
    }

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
        formik.setFieldValue('multiple_type', event.target.value)
        formik.setFieldValue('cost_per_multiple', getSliderInitialValue(costStructure, event.target.value))
    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        setSelectedType(costStructure.multiple_type[0])
        formik.setFieldValue("multiple_type", costStructure.multiple_type[0])
        formik.setFieldValue("multiple", costStructure.multiple)
        formik.setFieldValue("frequency", costStructure.frequency)
        formik.setFieldValue("cost_per_multiple", getSliderInitialValue(costStructure, costStructure.multiple_type[0]))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costStructure]);

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editOperatingDriverId) {
            setPositiveDriver(editOperatingDriver["positive_driver"])
            setSelectedType(editOperatingDriver["multiple_type"])

            formik.setFieldValue("multiple_type", editOperatingDriver["multiple_type"])
            formik.setFieldValue("cost_per_multiple", editOperatingDriver["cost_per_multiple"])
            formik.setFieldValue("efficiency_gain", editOperatingDriver["efficiency_gain"])
            formik.setFieldValue("share_of_cost", editOperatingDriver["share_of_cost"])
            formik.setFieldValue("multiple", editOperatingDriver["multiple"])
            formik.setFieldValue("multiple_value", editOperatingDriver["multiple_value"])
            formik.setFieldValue("frequency", editOperatingDriver["frequency"])
            formik.setFieldValue("positive_driver", editOperatingDriver["positive_driver"])
            formik.setFieldValue("outcome", editOperatingDriver["outcome"])
            formik.setFieldValue("driver_name", editOperatingDriver["driver_name"])
            formik.setFieldValue("comment", editOperatingDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editOperatingDriverId])

    useEffect(() => {
        if (!editOperatingDriverId) {
            formik.setFieldValue("driver_name", `${formatString(selectedType)} ${positiveDriver ? 'Savings' : 'Extra'}`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver, selectedType])

    const initialValues = {
        id: null,
        cost_type: 'operating',
        outcome: '',
        driver_name: `${formatString(selectedType)} ${positiveDriver ? 'Savings' : 'Extra'}`,
        comment: '',
        positive_driver: true,
        type: costStructure.type,
        currency: currency,
        multiple_type: selectedType,
        multiple: costStructure.multiple,
        multiple_value: 0,
        frequency: costStructure.multiple,
        cost_per_multiple: getSliderInitialValue(costStructure, selectedType),
        efficiency_gain: 0,
        share_of_cost: 100
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};
        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }
        if (!isNumeric(values.efficiency_gain)) {
            errors.efficiency_gain = 'Must be numeric value';
        } else if (!isPositive(values.efficiency_gain)) {
            errors.efficiency_gain = 'Must be positive or zero';
        } else if (values.efficiency_gain > 100) {
            errors.efficiency_gain = 'Not more than 100';
        }
        if (!isNumeric(values.share_of_cost)) {
            errors.share_of_cost = 'Must be numeric value';
        } else if (!isPositive(values.share_of_cost)) {
            errors.share_of_cost = 'Must be positive or zero';
        } else if (values.share_of_cost > 100) {
            errors.share_of_cost = 'Not more than 100';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setSelectedType(costStructure.multiple_type[0])
            setPositiveDriver(true)
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            formik.setFieldValue("multiple_type", costStructure.multiple_type[0])
            formik.setFieldValue("multiple", costStructure.multiple)
            formik.setFieldValue("frequency", costStructure.frequency)
            formik.setFieldValue("cost_per_multiple", getSliderInitialValue(costStructure, costStructure.multiple_type[0]))
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1200, mt: 2 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={3}>
                {costStructure.multiple_type.map(type => {
                    if (type === selectedType) {
                        return (
                            <React.Fragment key={type}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        2. Cost savings
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            Sub Type
                                        </Typography>
                                        <Box>
                                            <Select
                                                open={open}
                                                onClose={handleClose}
                                                onOpen={handleOpen}
                                                value={formik.values.multiple_type}
                                                onChange={handleTypeChange}
                                                size='small'
                                            >
                                                {costStructure.multiple_type.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                            {firstCharToUpper(type)}
                                                            {costStructure.cost_per_multiple[type]?.user_created && toolTipUserIcon()}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={3}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            {`${costStructure.frequency === 'year' ? 'Annual cost' : 'Cost'} per ${costStructure.multiple}`}
                                        </Typography>
                                        < Slider
                                            name="cost_per_multiple"
                                            value={formik.values.cost_per_multiple}
                                            onChange={formik.handleChange}
                                            valueLabelDisplay="auto"
                                            marks={createSliderMarks(costStructure.cost_per_multiple[type])}
                                            min={costStructure.cost_per_multiple[type].lower * 0}
                                            max={costStructure.cost_per_multiple[type].higher * 1.2}
                                            step={getMagnitude(costStructure.cost_per_multiple[type].lower) / 10}
                                            valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), costStructure.cost_per_multiple[type].currency, null)}
                                            sx={{ maxWidth: 300 }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={9} />
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                % of cost{tooltip(tooltipText.shareOfCost)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                {variableName}{tooltip(tooltipText.efficiency)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8} />
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="share_of_cost"
                                                endAdornment="%"
                                                value={formik.values.share_of_cost}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("share_of_cost", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "share_of_cost") && Boolean(getIn(formik.errors, "share_of_cost"))}
                                                helperText={getIn(formik.touched, "share_of_cost") && getIn(formik.errors, "share_of_cost")}
                                            />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="efficiency_gain"
                                                endAdornment="%"
                                                value={formik.values.efficiency_gain}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("efficiency_gain", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "efficiency_gain") && Boolean(getIn(formik.errors, "efficiency_gain"))}
                                                helperText={getIn(formik.touched, "efficiency_gain") && getIn(formik.errors, "efficiency_gain")}
                                                greenBackground={positiveDriver}
                                                redBackground={!positiveDriver}
                                            />
                                        </Grid>
                                        <Grid item xs={10} />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        3. Driver details
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                                        </Typography>
                                        <GreenRedCompactSwitch
                                            name="positive_driver"
                                            leftLabel="Negative"
                                            rightLabel="Positive"
                                            value={formik.values.positive_driver}
                                            onChange={(event) => {
                                                formik.handleChange(event)
                                                setPositiveDriver(!positiveDriver)
                                            }}
                                        />

                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                {!outcome &&
                                    (<React.Fragment>
                                        <Grid item xs={2}>
                                            <Stack direction="column" gap={1}>
                                                <Typography variant="caption">
                                                    Outcome
                                                </Typography>
                                                <Box>
                                                    <Select
                                                        open={openOutcome}
                                                        onClose={handleOutcomeClose}
                                                        onOpen={handleOutcomeOpen}
                                                        value={formik.values.outcome}
                                                        onChange={handleOutcomeChange}
                                                        size='small'
                                                    >
                                                        {outcomeList.map((outcome) => (
                                                            <MenuItem key={outcome} value={outcome}>
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                    {outcome}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={10} />
                                    </React.Fragment>)
                                }
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Name of driver
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Comment
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} />
                                        <Grid item xs={3}>
                                            <TextFieldCustom
                                                name="driver_name"
                                                value={formik.values.driver_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                                helperText={
                                                    (getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")) ||
                                                        (getIn(formik.errors, "driver_name") && getIn(formik.errors, "driver_name").includes("already"))
                                                        ? getIn(formik.errors, "driver_name")
                                                        : ''
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextFieldCustom
                                                name="comment"
                                                value={formik.values.comment}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        )
                    }
                    else {
                        return null;
                    }
                })
                }
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default CostValueDriverOperatingForm                